/* eslint-disable no-unused-vars */
import React, { useState } from 'react';
import { Modal, Table, Button } from 'antd';
import { FaEye, FaEyeSlash, FaPen, FaPlus } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { FormProvider, useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import dayjs from 'dayjs';
import { AlertConfirm, AlertSuccess } from '../../../../util/Alert';
import fitCheckingServices from '../../../../../services/fit-checking.services';
import {
  branchActivityFormDefaultValues,
  branchActivityFormSchema,
} from '../dto/branchActivity';
import BranchActivityModal from './BranchActivityModal';
import usePaginationHook from '../../../../../utils/usePagination';

const pageSize = 10;

const BranchActivityListModal = ({ open, handleClose, branchId }) => {
  const [formModalOpen, setFormModalOpen] = useState(false);

  const {
    data: activityLists,
    setData,
    page,
    setPage,
    total,
    setTotal,
  } = usePaginationHook();
  const queryClient = useQueryClient();

  const formMethods = useForm({
    defaultValues: branchActivityFormDefaultValues,
    resolver: branchActivityFormSchema(),
  });

  const _HandleFormModalOpen = (formType) => {
    formMethods.setValue('formType', formType);
    setFormModalOpen(true);
  };

  const _HandleFormModalClose = () => {
    formMethods.reset(branchActivityFormDefaultValues);
    setFormModalOpen(false);
  };

  fitCheckingServices.useQueryGetBranchActivities(
    { take: pageSize, page, branchId },
    (data) => {
      setData(data.data);
      setTotal(data.meta.total);
    },
  );

  const { mutate: deleteActivity } =
    fitCheckingServices.useMutationDeleteBranchActivity(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-checking-branch-activities'],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['get-checking-branch-histories'],
        refetchType: 'all',
      });
      AlertSuccess('ลบข้อมูลสำเร็จ');
    });

  const { mutate: updateDisplay } =
    fitCheckingServices.useMutationUpdateBranchActivityDisplay(() => {
      queryClient.invalidateQueries({
        queryKey: ['get-checking-branch-activities'],
        refetchType: 'all',
      });
      queryClient.invalidateQueries({
        queryKey: ['get-checking-branch-histories'],
        refetchType: 'all',
      });
      AlertSuccess('แก้ไขการแสดงผลสำเร็จ');
    });

  const columns = [
    {
      title: 'NO',
      dataIndex: 'index',
      key: 'index',
      render: (_, _record, index) => (
        <span>{(page - 1) * pageSize + index + 1}</span>
      ),
    },
    {
      title: 'รายละเอียด',
      dataIndex: 'desctiption',
      key: 'desctiption',
      render: (_, record) => (
        <span
          style={{
            maxWidth: 200,
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {record.desctiption}
        </span>
      ),
    },
    {
      title: 'วันที่เริ่มต้น',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (_, record) => (
        <span>
          {record.startDate
            ? dayjs(record.startDate).format('DD/MM/YYYY HH:mm')
            : '-'}
        </span>
      ),
    },
    {
      title: 'วันที่สิ้นสุด',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (_, record) => (
        <span>
          {record.endDate
            ? dayjs(record.endDate).format('DD/MM/YYYY HH:mm')
            : '-'}
        </span>
      ),
    },
    {
      title: '',
      key: 'action',
      render: (_, record) => (
        <div
          style={{ display: 'flex', justifyContent: 'flex-end' }}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          {record.display ? (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('ปิดการแสดงผล');
                if (confirm) updateDisplay({ id: record.id, display: false });
              }}
            >
              <FaEye />
            </div>
          ) : (
            <div
              style={{ cursor: 'pointer', marginLeft: 10 }}
              onClick={async () => {
                const confirm = await AlertConfirm('เปิดการแสดงผล');
                if (confirm) updateDisplay({ id: record.id, display: true });
              }}
            >
              <FaEyeSlash />
            </div>
          )}
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={() => {
              formMethods.setValue('id', record.id);
              _HandleFormModalOpen('edit');
            }}
          >
            <FaPen />
          </div>
          <div
            style={{ cursor: 'pointer', marginLeft: 10 }}
            onClick={async () => {
              const confirm = await AlertConfirm('ลบข้อมูล');
              if (confirm) deleteActivity({ id: record.id });
            }}
          >
            <MdDelete />
          </div>
        </div>
      ),
    },
  ];

  return (
    <Modal
      title="รายการกิจกรรมสาขา"
      visible={open}
      onCancel={handleClose}
      footer={null}
      width="70vw"
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: 16,
        }}
      >
        <Button
          onClick={() => {
            _HandleFormModalOpen('create');
          }}
          type="primary"
        >
          <FaPlus className="mr-2" />
          เพิ่มกิจกรรมสาขา
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={activityLists}
        style={{ backgroundColor: '#fff' }}
        rowKey="id"
        scroll={{ x: 'max-content' }}
        onChange={({ current }) => {
          setPage(current);
        }}
        pagination={{
          pageSize,
          total,
        }}
      />
      <FormProvider {...formMethods}>
        <BranchActivityModal
          open={formModalOpen}
          handleClose={_HandleFormModalClose}
          branchId={branchId}
        />
      </FormProvider>
    </Modal>
  );
};

export default BranchActivityListModal;
